import type { FollowType } from '@/utils/following';
import { Overlay } from '@/components/raven/Overlay';
import { useSiteContext } from '@/context/SiteContext';
import { useOverlayContext } from '@/context/OverlayContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { LoginForm } from '@/components/raven/global/LoginForm';
import type { AppSettings } from '@/utils/data/fetch-app-settings';
import { FollowingState } from '@/components/raven/global/FollowButton/types';
import { LOGIN_SOURCE, TERMS_OF_USE_URL, PRIVACY_TERMS_URL } from '@/constants';
import React, {
	useMemo,
	useState,
	useEffect,
	useCallback,
	type ChangeEvent,
} from 'react';
import {
	LoginFormStatus,
	type LoginFormProps,
} from '@/components/raven/global/LoginForm/types';
import {
	LoginType,
	OAuthPhase,
	EmailLinkPhase,
	useLoginContext,
	type OAuthProviderType,
} from '@/context/LoginContext';

/* --- START: Const and Types --- */
export const FOLLOW_LOGIN_OVERLAY_VARIANT = 'follow-login';
/* --- END: Types --- */

/* --- START: HandleEmailLinkSignIn --- */
export interface HandleEmailLinkSignInParams {
	email: string;
	followableId: string;
	followType: FollowType | null;
	handleSendEmailLink: (email: string, url: string) => Promise<void>;
	setLoginType: React.Dispatch<React.SetStateAction<LoginType | null>>;
}
export const handleEmailLinkSignIn = ({
	email,
	followableId,
	followType,
	handleSendEmailLink,
	setLoginType,
}: HandleEmailLinkSignInParams): Promise<void> => {
	setLoginType(LoginType.EMAIL_LINK);
	const url = new URL(window.location.href);
	url.searchParams.set(LOGIN_SOURCE, 'loginToFollow');
	url.searchParams.set('followableId', followableId);

	if (followType) {
		url.searchParams.set('followType', followType);
	}

	return handleSendEmailLink(email, url.toString());
};
/* --- END: HandleEmailLinkSignIn --- */

/* --- START: CheckFirebaseLogin --- */
export interface CheckFirebaseLoginParams {
	handleAfterFirebaseLogin: () => void;
}
export const checkFirebaseLogin = ({
	handleAfterFirebaseLogin,
}: CheckFirebaseLoginParams): void => {
	const searchParams = new URLSearchParams(window.location.search);
	if (
		searchParams.get('apiKey') &&
		searchParams.get('followableId') &&
		searchParams.get('followType') &&
		searchParams.get(LOGIN_SOURCE) === 'loginToFollow'
	) {
		handleAfterFirebaseLogin();
	}
};
/* --- END: CheckFirebaseLogin --- */

/* --- START: GetLoginFormStatus --- */
export interface GetLoginFormStatusParams {
	emailLinkPhase: EmailLinkPhase | null;
	followingState: FollowingState;
	isFetchingAfterLogin: boolean;
	oauthPhase: null | OAuthPhase;
	rgisCookie: null | string;
}
export const getLoginFormStatus = ({
	emailLinkPhase,
	followingState,
	isFetchingAfterLogin,
	oauthPhase,
	rgisCookie,
}: GetLoginFormStatusParams): LoginFormStatus => {
	// Successfully logged in, but still fetching follow info.
	if (rgisCookie && isFetchingAfterLogin) {
		return LoginFormStatus.LOADING;
	}

	// Successfully logged in, fetched follow info, but failed to enable follow.
	if (
		rgisCookie &&
		!isFetchingAfterLogin &&
		followingState !== FollowingState.Active
	) {
		return LoginFormStatus.ERROR;
	}

	// Successfully logged in, fetched follow info, and successfully enabled follow.
	if (
		rgisCookie &&
		!isFetchingAfterLogin &&
		followingState === FollowingState.Active
	) {
		return LoginFormStatus.SUCCESS;
	}

	// Handle OAuth phases
	switch (oauthPhase) {
		case OAuthPhase.CHOOSE:
			return LoginFormStatus.ERROR;
		case OAuthPhase.SIGNING_IN:
			return LoginFormStatus.LOADING;
		default:
	}

	// Handle Email Link phases
	switch (emailLinkPhase) {
		case EmailLinkPhase.CONFIRM:
			return LoginFormStatus.ERROR;
		case EmailLinkPhase.COMPLETED:
		case EmailLinkPhase.SENT:
			return LoginFormStatus.SUCCESS;
		case EmailLinkPhase.SENDING_EMAIL:
		case EmailLinkPhase.SIGNING_IN:
			return LoginFormStatus.LOADING;
		case EmailLinkPhase.INPUT:
		default:
			return LoginFormStatus.IDLE;
	}
};
/* --- END: GetLoginFormStatus --- */

/* --- START: HandleOAuthSignIn --- */
export interface HandleOAuthSignInParams {
	provider: OAuthProviderType;
	setLoginType: React.Dispatch<React.SetStateAction<LoginType | null>>;
	signInOAuth: (provider: OAuthProviderType) => void;
}
export const handleOAuthSignIn = ({
	provider,
	setLoginType,
	signInOAuth,
}: HandleOAuthSignInParams): void => {
	setLoginType(LoginType.OAUTH);
	signInOAuth(provider);
};
/* --- END: HandleOAuthSignIn --- */

/* --- START: GetLoginFormProps --- */
export interface GetLoginFormPropsParams {
	handleFollowEmailLinkSignIn: (email: string) => Promise<void>;
	handleOAuthSignInCallback: (provider: OAuthProviderType) => void;
	loginType: LoginType | null;
	onEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
	rgisCookie: null | string;
	settings: AppSettings['settings'];
	siteName: string;
	status: LoginFormStatus;
	userEmail: string;
}
export const getLoginFormProps = ({
	handleFollowEmailLinkSignIn,
	handleOAuthSignInCallback,
	loginType,
	onEmailChange,
	rgisCookie,
	settings,
	siteName,
	status,
	userEmail,
}: GetLoginFormPropsParams): LoginFormProps => ({
	buttonText: 'Continue with Email',
	checkboxText: (
		<>
			By signing up I agree to the {siteName}&nbsp;
			<a href={TERMS_OF_USE_URL} rel="noopener noreferrer" target="_blank">
				Terms of Use
			</a>
			. I acknowledge that I have read the&nbsp;
			<a href={PRIVACY_TERMS_URL} rel="noopener noreferrer" target="_blank">
				Privacy Policy
			</a>
			. You may unsubscribe from email communication at anytime.
		</>
	),
	// rgisCookie present indicates successful login but failed to enable follow.
	// no rgisCookie indicates failed login.
	descriptionError: rgisCookie
		? "Sorry, we couldn't add this to your follows. Please try again later."
		: 'Please try again or use a different sign-in method.',
	descriptionLoading:
		loginType === LoginType.EMAIL_LINK
			? 'Please wait while we send you a sign-in link.'
			: 'Please wait while we sign you in.',
	descriptionSuccess:
		loginType === LoginType.EMAIL_LINK
			? `Check your email ${userEmail} to complete the sign-in process. If it's missing, check spam or request a new one.`
			: "You are now following! You'll receive updates about this item.",
	handleOAuthSignIn: handleOAuthSignInCallback,
	onContinue: handleFollowEmailLinkSignIn,
	onEmailChange,
	settings,
	status,
	textFieldPlaceholder: 'Enter your email',
	titleDefault: 'Sign-in to follow',
	// rgisCookie present indicates successful login but failed to enable follow.
	// no rgisCookie indicates failed login.
	titleError: rgisCookie ? 'Oops! Something went wrong' : 'Sign-in failed',
	titleLoading:
		loginType === LoginType.EMAIL_LINK
			? 'Sending sign-in link...'
			: 'Signing in...',
	titleSuccess:
		loginType === LoginType.EMAIL_LINK
			? "You're almost there!"
			: 'You are now following!',
});
/* --- END: GetLoginFormProps --- */

interface FollowLoginOverlayProps {
	followableId: string;
	followingState: FollowingState;
	followType: FollowType | null;
	isFetchingAfterLogin: boolean;
}
export const FollowLoginOverlay: React.FC<FollowLoginOverlayProps> = ({
	followableId,
	followingState,
	followType,
	isFetchingAfterLogin,
}) => {
	const { settings } = useAppSettings();
	const { closeVariant } = useOverlayContext();
	const {
		emailLinkPhase,
		handleAfterFirebaseLogin,
		handleSendEmailLink,
		oauthPhase,
		rgisCookie,
		signInOAuth,
	} = useLoginContext();
	const {
		config: { siteName },
	} = useSiteContext();

	const [userEmail, setUserEmail] = useState('');
	const [loginType, setLoginType] = useState<LoginType | null>(null);

	const onEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setUserEmail(e.target.value);
	}, []);

	const handleFollowEmailLinkSignIn = useCallback(
		(email: string) =>
			handleEmailLinkSignIn({
				email,
				followableId,
				followType,
				handleSendEmailLink,
				setLoginType,
			}),
		[handleSendEmailLink, followableId, followType],
	);

	const handleOAuthSignInCallback = useCallback(
		(provider: OAuthProviderType) => {
			handleOAuthSignIn({
				provider,
				setLoginType,
				signInOAuth,
			});
		},
		[signInOAuth],
	);

	useEffect(() => {
		checkFirebaseLogin({
			handleAfterFirebaseLogin,
		});
	}, [handleAfterFirebaseLogin]);

	const status = useMemo(
		() =>
			getLoginFormStatus({
				emailLinkPhase,
				followingState,
				isFetchingAfterLogin,
				oauthPhase,
				rgisCookie,
			}),
		[
			rgisCookie,
			oauthPhase,
			emailLinkPhase,
			isFetchingAfterLogin,
			followingState,
		],
	);

	const loginFormProps: LoginFormProps = useMemo(
		() =>
			getLoginFormProps({
				handleFollowEmailLinkSignIn,
				handleOAuthSignInCallback,
				loginType,
				onEmailChange,
				rgisCookie,
				settings,
				siteName,
				status,
				userEmail,
			}),
		[
			userEmail,
			handleFollowEmailLinkSignIn,
			handleOAuthSignInCallback,
			loginType,
			onEmailChange,
			rgisCookie,
			settings,
			siteName,
			status,
		],
	);

	return (
		<Overlay
			className="right"
			onClose={() => {
				closeVariant(FOLLOW_LOGIN_OVERLAY_VARIANT);
			}}
			variant={FOLLOW_LOGIN_OVERLAY_VARIANT}
		>
			<LoginForm location="follow-drawer" {...loginFormProps} />
		</Overlay>
	);
};
